import { useMemo } from "react";
import type { TaskStatus } from "@/api/tasks";
import type { CardPreviewProps } from "@/components/shapes/card-previews";
import { PreviewSummary } from "@/components/shapes/shared/card-preview-parts";
import { translate, useAppLanguage } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";
import { useFrameBoardTasks } from "./task-display-filters";
import { TASK_STATUS_LABEL_KEYS } from "./task-metadata-chips";

/** "3 In progress · 1 Ready for review · 12 Done", in board column order. */
function taskCountsLine(statuses: readonly TaskStatus[]): string {
  const counts = new Map<TaskStatus, number>();
  for (const status of statuses) {
    counts.set(status, (counts.get(status) ?? 0) + 1);
  }
  return (Object.keys(TASK_STATUS_LABEL_KEYS) as TaskStatus[])
    .filter((status) => counts.has(status))
    .map(
      (status) =>
        `${counts.get(status)} ${translate(framesTaskboardMessages, TASK_STATUS_LABEL_KEYS[status])}`,
    )
    .join(" · ");
}

/** A task board: its title and its own project's task counts by status. */
export function TaskboardCardPreview({ frame, density }: CardPreviewProps) {
  const tasks = useFrameBoardTasks(frame);
  // The status names are translated text, so the line follows the language.
  const language = useAppLanguage();
  const summary = useMemo(
    () =>
      language && tasks ? taskCountsLine(tasks.map((task) => task.status)) : "",
    [tasks, language],
  );
  return <PreviewSummary frame={frame} density={density} summary={summary} />;
}
